import { create } from 'zustand';
import type { ID } from 'types';
import type {
  ColumnKey,
  IdErrorChange,
  IdErrorState,
  LotsIdErrors,
} from '@features/buyer-line-items-pagination/types/id-error-state';

const EMPTY_LOT_ERRORS: IdErrorState = Object.freeze({}) as IdErrorState;

interface IdErrorsState {
  clearAll: () => void;
  clearColumn: (columnKey: ColumnKey) => void;
  clearRow: (lotId: ID, lineItemId: ID) => void;
  lotsIdErrors: LotsIdErrors;
  setCellValidity: (change: IdErrorChange) => void;
}

const withRow = (lotErrors: IdErrorState, rowId: string, columnKeys: ColumnKey[]): IdErrorState => {
  const otherRows = Object.fromEntries(Object.entries(lotErrors).filter(([id]) => id !== rowId));
  return columnKeys.length ? { ...otherRows, [rowId]: columnKeys } : otherRows;
};

export const useIdErrorsStore = create<IdErrorsState>((set, get) => ({
  clearAll: () => {
    if (Object.keys(get().lotsIdErrors).length) set({ lotsIdErrors: {} });
  },
  clearColumn: columnKey => {
    const key = String(columnKey);
    const { lotsIdErrors } = get();
    let changed = false;
    const next = Object.fromEntries(Object.entries(lotsIdErrors).map(([lotId, lotErrors]) => {
      const rowIds = Object.keys(lotErrors).filter(rowId => lotErrors[rowId].includes(key));
      if (!rowIds.length) return [lotId, lotErrors];
      changed = true;
      return [lotId, rowIds.reduce(
        (errors, rowId) => withRow(errors, rowId, lotErrors[rowId].filter(k => k !== key)),
        lotErrors,
      )];
    }));
    if (changed) set({ lotsIdErrors: next });
  },
  clearRow: (lotId, lineItemId) => {
    const rowId = String(lineItemId);
    const { lotsIdErrors } = get();
    const lotErrors = lotsIdErrors[lotId];
    if (!lotErrors?.[rowId]) return;
    set({ lotsIdErrors: { ...lotsIdErrors, [lotId]: withRow(lotErrors, rowId, []) } });
  },
  lotsIdErrors: {},
  setCellValidity: ({ columnKey, isValid, lineItemId, lotId }) => {
    const key = String(columnKey);
    const rowId = String(lineItemId);
    const { lotsIdErrors } = get();
    const lotErrors = lotsIdErrors[lotId] ?? EMPTY_LOT_ERRORS;
    const columnKeys = lotErrors[rowId] ?? [];
    const hasError = columnKeys.includes(key);
    if (isValid !== hasError) return;

    const nextKeys = isValid ? columnKeys.filter(k => k !== key) : [...columnKeys, key];
    set({ lotsIdErrors: { ...lotsIdErrors, [lotId]: withRow(lotErrors, rowId, nextKeys) } });
  },
}));

export const useLotsIdErrors = (): LotsIdErrors => useIdErrorsStore(s => s.lotsIdErrors);
export const useLotIdErrors = (lotId: ID): IdErrorState => (
  useIdErrorsStore(s => s.lotsIdErrors[lotId] ?? EMPTY_LOT_ERRORS)
);

export const idErrorsStore = {
  clearAll: (): void => useIdErrorsStore.getState().clearAll(),
  clearColumn: (columnKey: ColumnKey): void => useIdErrorsStore.getState().clearColumn(columnKey),
  clearRow: (lotId: ID, lineItemId: ID): void => useIdErrorsStore.getState().clearRow(lotId, lineItemId),
  setCellValidity: (change: IdErrorChange): void => useIdErrorsStore.getState().setCellValidity(change),
};
